import type { enAccounts } from './en-accounts.js';

/** 1 elev, 2 elevi, 20 de elevi. */
const plural = (n: number, one: string, many: string) => {
  if (n === 1) return `1 ${one}`;
  const r = n % 100;
  return `${n} ${r === 0 || r >= 20 ? `de ${many}` : many}`;
};

export const roAccounts: typeof enAccounts = {
  auth: {
    email: 'Email',
    password: 'Parolă',
    show: 'Arată',
    hide: 'Ascunde',
    loginTitle: 'Intră în cont',
    loginSubmit: 'Intră',
    forgotLink: 'Ți-ai uitat parola?',
    newHere: 'Ești nou aici?',
    createAccount: 'Fă-ți un cont',
    invalid: 'Email sau parolă greșită.',
    disabled: 'Contul acesta a fost dezactivat. Întreabă-ți profesorul sau scrie-ne.',
    tooMany: 'Prea multe încercări. Mai așteaptă un minut.',
    signupTitle: 'Fă-ți un cont',
    signupText: 'Îți păstrezi lucrul pe orice calculator, intri în clasa ta și primești teme.',
    displayName: 'Numele pe care îl văd ceilalți',
    displayNameHint: 'Poate fi o poreclă — nu e nevoie de numele complet.',
    passwordHint: 'Cel puțin 8 caractere',
    born: 'Luna și anul nașterii',
    month: 'Luna',
    year: 'Anul',
    parentEmail: 'Emailul unui părinte',
    parentHint:
      'Ai sub 16 ani, așa că un părinte trebuie să fie de acord. Îi trimitem un singur email; până răspunde, nu poți intra într-o clasă.',
    wantsTeacher: 'Sunt profesor (un administrator verifică și aprobă)',
    acceptPrivacy: 'Am citit ce păstrează PebbleCode despre mine:',
    privacyLink: 'nota de confidențialitate',
    signupSubmit: 'Creează contul',
    haveAccount: 'Ai deja cont?',
    emailTaken: 'Există deja un cont cu acest email. Vrei să intri în el?',
    parentRequired: 'Adaugă emailul unui părinte.',
    parentSame: 'Emailul părintelui trebuie să fie diferit de al tău.',
    forgotTitle: 'Ți-ai uitat parola?',
    forgotText: 'Scrie emailul. Dacă are un cont, îți trimitem un link ca să alegi o parolă nouă.',
    forgotSubmit: 'Trimite linkul',
    forgotSent: (email: string) =>
      `Dacă există un cont pentru ${email}, linkul e pe drum. Verifică emailul (și folderul spam).`,
    backToLogin: 'Înapoi la intrare',
    resetTitle: 'Alege o parolă nouă',
    newPassword: 'Parola nouă',
    resetSubmit: 'Salvează și intră',
    linkInvalid: 'Linkul nu mai merge — a fost deja folosit sau a expirat.',
    askNewLink: 'Cere un link nou',
    verifying: 'Confirmăm emailul…',
    verified: 'Emailul tău este confirmat. Mulțumim!',
    continue: 'Mergi la PebbleCode',
    unverified: (email: string) => `Confirmă-ți emailul: am trimis un link la ${email}.`,
    consentPending: (parent: string) =>
      `Un părinte trebuie să fie de acord înainte să intri într-o clasă. I-am scris la ${parent}.`,
    resend: 'Trimite din nou',
    resent: 'Trimis!',
    claimed: (projects: number, points: number) =>
      `Ce ai lucrat pe acest calculator este acum în contul tău: ${plural(projects, 'proiect', 'proiecte')}, ${plural(points, 'punct', 'puncte')}.`,
  },
  menu: {
    label: 'Meniul contului',
    login: 'Intră în cont',
    signup: 'Cont nou',
    account: 'Contul meu',
    classes: 'Clasele mele',
    exercises: 'Exercițiile mele',
    admin: 'Utilizatori',
    logout: 'Ieși din cont',
  },
  account: {
    title: 'Contul meu',
    profile: 'Profil',
    save: 'Salvează',
    saved: 'Salvat',
    role: { student: 'Elev', teacher: 'Profesor', admin: 'Administrator' },
    youAre: 'Tipul contului',
    askTeacher: 'Sunt profesor — cer acces de profesor',
    teacherPending: 'Cererea ta de profesor așteaptă un administrator.',
    teacherRejected:
      'Un administrator a refuzat cererea de profesor. Scrie-ne dacă este o greșeală.',
    passwordTitle: 'Parolă',
    currentPassword: 'Parola actuală',
    changePassword: 'Schimbă parola',
    passwordChanged: 'Parola a fost schimbată. Celelalte dispozitive au ieșit din cont.',
    wrongPassword: 'Parola este greșită.',
    parentTitle: 'Acordul părintelui',
    consentGranted: 'Un părinte și-a dat acordul. Contul tău este complet.',
    deleteTitle: 'Șterge contul',
    deleteText:
      'Șterge imediat contul, proiectele, punctele, clasele și înscrierile la clase. Nu se mai poate reveni.',
    deletePassword: 'Scrie parola ca să confirmi',
    deleteButton: 'Șterg contul pentru totdeauna',
  },
  admin: {
    title: 'Utilizatori',
    search: 'Caută după nume sau email',
    filters: {
      all: 'Toți',
      'teacher-requests': 'Cereri de profesor',
      teachers: 'Profesori',
      admins: 'Administratori',
      disabled: 'Dezactivați',
    },
    count: (n: number) => plural(n, 'utilizator', 'utilizatori'),
    name: 'Nume',
    role: 'Rol',
    status: 'Stare',
    joined: 'Înscris',
    actions: 'Acțiuni',
    approve: 'Aprobă ca profesor',
    reject: 'Refuză',
    disable: 'Dezactivează',
    enable: 'Activează',
    delete: 'Șterge',
    deleteConfirm: (name: string) => `Ștergi contul lui ${name} și tot ce a lucrat?`,
    sendReset: 'Trimite link de parolă',
    resetSent: 'Link trimis',
    unverified: 'email neconfirmat',
    consentPending: 'așteaptă un părinte',
    requested: 'vrea să predea',
    rejected: 'cerere de profesor refuzată',
    disabled: 'dezactivat',
    none: 'Niciun utilizator găsit.',
    more: 'Arată mai mulți',
    changeRole: (name: string) => `Rolul lui ${name}`,
  },
  parent: {
    consentTitle: (name: string) => `${name} vrea un cont PebbleCode`,
    consentIntro: (name: string, email: string) =>
      `${name} (${email}) și-a făcut cont pe PebbleCode, unde elevii învață programare cu blocuri și cod adevărat, și a dat adresa dumneavoastră ca părinte.`,
    keepTitle: 'Ce păstrăm',
    keep: [
      'Emailul elevului, numele ales și luna și anul nașterii.',
      'Emailul dumneavoastră, ca să vă întrebăm și ca să puteți retrage acordul.',
      'Proiectele și punctele elevului și clasele în care intră.',
    ],
    notTitle: 'Ce nu facem niciodată',
    not: [
      'Fără reclame, fără vânzare de date, fără profil public.',
      'Ceilalți elevi nu îi văd emailul.',
    ],
    approve: 'Sunt de acord',
    deny: 'Nu sunt de acord — ștergeți contul',
    denyConfirm: 'Ștergem acum contul și toate datele lui?',
    approved:
      'Mulțumim — contul este complet. V-am trimis pe email un link cu care îl puteți vedea sau vă puteți retrage acordul mai târziu.',
    denied: 'Contul și toate datele lui au fost șterse.',
    manageTitle: (name: string) => `Contul PebbleCode al lui ${name}`,
    manageText:
      'Ați fost de acord cu acest cont. Vă puteți retrage oricând: contul și tot ce conține sunt șterse imediat.',
    withdraw: 'Îmi retrag acordul și șterg contul',
    withdrawConfirm: 'Ștergem acum contul și toate datele lui? Nu se mai poate reveni.',
    withdrawn: 'Contul și toate datele lui au fost șterse.',
  },
  privacy: {
    title: 'Notă de confidențialitate',
    draft: 'Ciornă — va fi verificată de un jurist înainte de lansare.',
    sections: [
      [
        'Fără cont',
        'Nu păstrăm nimic personal. Browserul ține o cheie aleatorie; proiectele și punctele sunt salvate doar sub acea cheie.',
      ],
      [
        'Cu cont',
        'Păstrăm emailul, numele ales, luna și anul nașterii, proiectele, punctele și clasele tale și când ai intrat ultima dată. Parola este păstrată doar ca amprentă ireversibilă.',
      ],
      [
        'Elevi sub 16 ani',
        'Păstrăm și emailul unui părinte și îi cerem acordul. Până atunci contul nu poate intra în clase; fără răspuns, este șters după 14 zile. Părintele își poate retrage oricând acordul, iar contul se șterge.',
      ],
      [
        'Profesori',
        'Un profesor vede numele și emailurile elevilor din clasele lui, temele lor și ce lucrează pentru ele. Elevii nu își văd unii altora emailurile.',
      ],
      [
        'Tutorele AI',
        'Întrebările către tutore trimit programul și exercițiul unui furnizor de AI, niciodată numele sau emailul tău.',
      ],
      [
        'Ștergere',
        'Îți poți șterge contul din pagina contului; totul se șterge imediat. Emailurile sunt trimise prin Resend.',
      ],
    ] as [string, string][],
  },
  classes: {
    title: 'Clasele mele',
    newClass: 'Clasă nouă',
    namePlaceholder: 'de ex. a VII-a B sau grupa de marți',
    create: 'Creează',
    emptyTeacher: 'Nicio clasă încă. Creează una și dă-le elevilor codul ei.',
    emptyStudent: 'Nu ești încă într-o clasă. Cere-i profesorului codul clasei.',
    students: (n: number) => plural(n, 'elev', 'elevi'),
    teacher: (name: string) => `Profesor: ${name}`,
    joinCode: 'Codul clasei',
    joinLink: 'Link de înscriere',
    newCode: 'Cod nou',
    newCodeConfirm: 'Faci un cod nou? Cel vechi nu mai merge; elevii deja înscriși rămân.',
    rename: 'Redenumește',
    delete: 'Șterge clasa',
    deleteConfirm: (name: string) =>
      `Ștergi ${name}? Se șterg și temele și predările elevilor (proiectele lor rămân).`,
    roster: 'Elevi',
    noStudents: 'Niciun elev încă. Trimite linkul sau codul clasei.',
    remove: 'Scoate',
    removeConfirm: (name: string) => `Îl scoți pe ${name} din clasă?`,
    leave: 'Ieși din clasă',
    leaveConfirm: (name: string) => `Ieși din ${name}?`,
    joinTitle: 'Intră într-o clasă',
    joinText: 'Scrie codul de 8 caractere primit de la profesor.',
    joinPlaceholder: 'CODUL CLASEI',
    join: 'Intră',
    joinInvalid: 'Codul unei clase are 8 litere și cifre.',
    joinNotFound: 'Nicio clasă nu are acest cod.',
    joinNeedsLogin: (name: string) => `Intră în cont sau fă-ți unul ca să intri în ${name}.`,
    joinConfirm: (name: string, teacher: string) => `Intri în ${name}, clasa lui ${teacher}?`,
    alreadyMember: (name: string) => `Ești deja în ${name}.`,
    notActive:
      'Contul tău are nevoie de un email confirmat (și, sub 16 ani, de acordul unui părinte) înainte să intri într-o clasă.',
    teachersOnly: 'Doar profesorii pot crea clase. Cere acces de profesor din pagina contului.',
    open: 'Deschide',
    back: 'Toate clasele',
  },
  classLive: {
    liveNow: 'Live acum',
    liveWith: (teacher: string, className: string) => `${teacher} · ${className}`,
    join: 'Intră',
    broadcast: 'Transmite unei clase',
    broadcastNone: 'Nicio clasă — doar linkuri',
    broadcastHint: (name: string) =>
      `Elevii din ${name} văd „Live acum” pe pagina principală și pot urmări (intrați în cont).`,
    start: 'Pornește live',
    classOnly: 'Sesiunea este pentru o clasă. Intră cu un cont din acea clasă ca să urmărești.',
    sharedWith: (name: string) => `Transmis pentru ${name}`,
  },
  homework: {
    title: 'Teme',
    none: 'Nicio temă acum.',
    due: (date: string) => `Termen: ${date}`,
    noDue: 'Fără termen',
    late: 'întârziat',
    status: {
      todo: 'Neînceput',
      started: 'În lucru',
      solved: 'Rezolvat',
      submitted: 'Predat',
    },
    open: 'Deschide',
    handIn: 'Predau',
    takeBack: 'Retrag',
    feedback: 'Părerea profesorului',
    projectNote: 'Aici nu e verificare automată: apasă Predau când ai terminat.',
    assign: 'Dă o temă',
    kind: {
      exercise: 'Un exercițiu din catalog',
      custom: 'Un exercițiu scris de mine',
      project: 'Un proiect de-al meu (ca program de pornire)',
    },
    pick: 'Alege…',
    itemTitle: 'Titlu',
    note: 'Notă pentru elevi (opțional)',
    dueDate: 'Termen (opțional)',
    create: 'Dă tema',
    progress: (done: number, total: number) => `${done} din ${total} gata`,
    startedCount: (n: number) => `${n} au început`,
    seeWork: 'Vezi lucrările',
    delete: 'Șterge',
    deleteConfirm: (title: string) => `Ștergi „${title}” și predările elevilor?`,
    noneTeacher: 'Nicio temă încă.',
    student: 'Elev',
    lastEdit: 'Ultima modificare',
    handedIn: 'Predat',
    openWork: 'Deschide lucrarea',
    notOpened: 'Nedeschisă încă',
    feedbackPlaceholder: 'Câteva cuvinte pentru elev…',
    saveFeedback: 'Salvează părerea',
    feedbackSaved: 'Părere salvată',
    reviewing: (student: string, title: string) => `${student} · ${title}`,
    viewOnly: 'doar vizualizare',
    back: 'Înapoi la temă',
    fromClass: (name: string) => `pentru ${name}`,
  },
  teach: {
    title: 'Exercițiile mele',
    newExercise: 'Exercițiu nou',
    empty: 'Nu ai scris încă niciun exercițiu.',
    edit: 'Modifică',
    delete: 'Șterge',
    deleteConfirm: (title: string) => `Ștergi „${title}”? Se șterg și temele care îl folosesc.`,
    formTitle: 'Titlu',
    summary: 'Rezumat pe un rând (opțional)',
    statement: 'Cerința',
    statementHint: 'Marchează codul cu `apostroafe inverse` și îngroșarea cu **stele**.',
    lang: 'Limba textului',
    category: 'Categorie',
    difficulty: 'Dificultate',
    examples: 'Exemple',
    examplesHint:
      'Programul este verificat pe fiecare exemplu. Și cele ascunse sunt verificate, dar elevii nu le văd.',
    input: 'Date de intrare (câte o valoare pe rând)',
    output: 'Ieșirea așteptată',
    hidden: 'Ascuns',
    addExample: 'Adaugă exemplu',
    removeExample: 'Șterge exemplul',
    exampleN: (i: number) => `Exemplul ${i}`,
    starter: 'Program de pornire (opțional)',
    starterNone: 'Program gol',
    starterKeep: 'Păstrează programul de pornire actual',
    save: 'Salvează',
    saved: 'Salvat',
    needVisible: 'Cel puțin un exemplu trebuie să fie vizibil.',
    hiddenFailing: (n: number) =>
      `Toate exemplele vizibile trec, dar ${n === 1 ? 'o verificare ascunsă nu trece' : `${n} verificări ascunse nu trec`}. Gândește-te la alte date de intrare.`,
    byTeacher: 'De la profesorul tău',
    back: 'Exercițiile mele',
  },
};
